import React from 'react';
import Link from 'next/link';
import styled from 'styled-components';
import config from '@/lib/config';
import { colors } from '@/styles/colors';
import TutorialNavigationBar from '@/components/navigation_bars/TutorialNavigationBar';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { LocaleNS } from '@/lib/locale-ns';
import { useTranslation } from 'next-i18next';

const TutorialsIndex: React.FC = () => {
    const { t, i18n } = useTranslation([LocaleNS.TUTORIALS], { keyPrefix: 'index' });

    return (
        <>
            <TutorialNavigationBar />
            <Container $isHebrew={i18n.language === 'he'}>
                <Heading $isHebrew={i18n.language === 'he'}>{t('title')}</Heading>
                <TutorialList>
                    <TutorialItem>
                        <Link href="/tutorials/wedding-invitation-guide">
                            <TutorialTitle>{t('wedding.title')}</TutorialTitle>
                            <TutorialDescription>{t('wedding.description')}</TutorialDescription>
                        </Link>
                    </TutorialItem>
                    <TutorialItem>
                        <Link href="/tutorials/bar-mitzva-invitation-guide">
                            <TutorialTitle>{t('bar-mitzva.title')}</TutorialTitle>
                            <TutorialDescription>{t('bar-mitzva.description')}</TutorialDescription>
                        </Link>
                    </TutorialItem>
                    <TutorialItem>
                        <Link href="/tutorials/common-mistakes-in-bar-mitzva-invitations">
                            <TutorialTitle>{t('common-mistakes.title')}</TutorialTitle>
                            <TutorialDescription>{t('common-mistakes.description')}</TutorialDescription>
                        </Link>
                    </TutorialItem>
                    <TutorialItem>
                        <Link href="/tutorials/birthday-invitation-guide">
                            <TutorialTitle>{t('birthday.title')}</TutorialTitle>
                            <TutorialDescription>{t('birthday.description')}</TutorialDescription>
                        </Link>
                    </TutorialItem>
                    <TutorialItem>
                        <Link href="/tutorials/brit-invitation-guide">
                            <TutorialTitle>{t('brit.title')}</TutorialTitle>
                            <TutorialDescription>{t('brit.description')}</TutorialDescription>
                        </Link>
                    </TutorialItem>
                </TutorialList>
            </Container>
        </>
    );
};

export const getStaticProps = async () => {
    return {
        props: {
            ...(await serverSideTranslations('he', [LocaleNS.COMMON, LocaleNS.NAV_BARS, LocaleNS.TUTORIALS]))
        }
    };
};

export default TutorialsIndex;

const Container = styled.div<{ $isHebrew: boolean }>`
    padding: 1rem;
    max-width: 960px;
    margin: 0 auto;
    direction: ${props => (props.$isHebrew ? 'rtl' : 'ltr')};
    min-height: calc(100vh - ${config.ui.navbarHeight}px);
    margin-block-start: calc(2rem + ${config.ui.navbarHeight}px);
`;

const Heading = styled.h1<{ $isHebrew: boolean }>`
    font-size: 2rem;
    font-weight: bold;
    margin-bottom: 2rem;
    text-align: ${props => (props.$isHebrew ? 'right' : 'left')};
    color: #1a1a1a;
`;

const TutorialList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`;

const TutorialItem = styled.li`
    margin-bottom: 1.5rem;
    display: block;
    padding: 1.5rem;
    background-color: ${colors.offWhite};
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    text-decoration: none;
    color: inherit;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
    }
`;

const TutorialTitle = styled.h2`
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: ${colors.primary};
`;

const TutorialDescription = styled.p`
    font-size: 1.125rem;
    line-height: 1.5;
    color: #333;
    margin: 0;
`;
